import { Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** antd Result statuses used by this page. */
type ResultStatus = 'warning' | '403' | 'error';

type CauseConfig = {
  status: ResultStatus;
  titleKey: string;
  subtitleKey: string;
  /** signout → clear session and restart; retry → bounce back to Entra login;
   *  none → no in-app action applies (e.g. an advisor whose session is fine
   *  but who needs to follow a fresh link from BMO). */
  action: 'signout' | 'retry' | 'none';
};

/**
 * Known `?cause=` values map to tailored copy. Role-assignment problems ask the
 * user to sign out and contact an admin; everything else (IdP errors, expired
 * state, code-exchange failures, or an unknown cause) falls back to a generic
 * "sign-in failed" with a retry that restarts the Entra flow.
 */
const CAUSES: Record<string, CauseConfig> = {
  'dual-role': {
    status: 'warning',
    titleKey: 'auth.dualRole.title',
    subtitleKey: 'auth.dualRole.subtitle',
    action: 'signout',
  },
  'no-role': {
    status: '403',
    titleKey: 'auth.noRole.title',
    subtitleKey: 'auth.noRole.subtitle',
    action: 'signout',
  },
  no_representative_code: {
    status: '403',
    titleKey: 'auth.noRepresentativeCode.title',
    subtitleKey: 'auth.noRepresentativeCode.subtitle',
    action: 'signout',
  },
  'advisor-entry-required': {
    status: 'warning',
    titleKey: 'auth.advisorEntryRequired.title',
    subtitleKey: 'auth.advisorEntryRequired.subtitle',
    action: 'none',
  },
  // Sent here by AuthGuard when the user returns to a tab that lost its slot to the tab cap
  // (see tabRegistry.ts). `retry` restarts the login flow, which lands the user back in a
  // freshly mounted tab that claims a slot of its own.
  'session-timeout': {
    status: 'warning',
    titleKey: 'auth.sessionTimeout.title',
    subtitleKey: 'auth.sessionTimeout.subtitle',
    action: 'retry',
  },
};

const FALLBACK: CauseConfig = {
  status: 'error',
  titleKey: 'auth.loginError.title',
  subtitleKey: 'auth.loginError.subtitle',
  action: 'retry',
};

/**
 * Generic auth error page. The backend and client-side guards redirect here with
 * a `?cause=` describing what went wrong (e.g. /auth/error?cause=dual-role).
 * Must stay outside the AuthGuard: sign-in failures leave no session, so guarding
 * this route would bounce the user straight back into the failing login.
 */
export function AuthErrorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const config = CAUSES[params.get('cause') ?? ''] ?? FALLBACK;

  const action =
    config.action === 'signout' ? (
      <Button type="primary" onClick={() => navigate('/auth/logout')}>
        {t('auth.signOut')}
      </Button>
    ) : config.action === 'retry' ? (
      // /auth/login is a server-side BFF endpoint, not a client route.
      <Button type="primary" onClick={() => window.location.assign('/auth/login')}>
        {t('auth.retry')}
      </Button>
    ) : undefined;

  return (
    <Result
      status={config.status}
      title={t(config.titleKey)}
      subTitle={t(config.subtitleKey)}
      extra={action}
    />
  );
}
